import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Link, Text, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Navigation, Pagination, Keyboard} from 'swiper/modules'
import {Swiper, SwiperSlide} from 'swiper/react'
import {ChevronRight, ChevronLeft} from '../../icons/arrows'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {remCalc} from '../../../theme/_utils'
import {SortingTile} from './sorting-tile'
import SanitizedHtml from '../../html-sanitizer'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {debounce} from 'lodash'

export const Sorting = ({
    title = '',
    variant = 'white',
    hasSlider = false,
    heading = '',
    bodycopy = '',
    sortingGroup = [],
    buttons = [],
}) => {
    const componentUniqueKey = title.replaceAll(/ /g, '-').toLowerCase()
    const isMobile = useBreakpointValue({base: true, md: false})
    const isSlider = hasSlider || isMobile || sortingGroup.length > 4
    const categoryData = useGetCategoriesData()
    const {locale} = useMultiSite()
    const buttonsProps =
        buttons?.length === 0
            ? []
            : buttons?.map((b) =>
                  getGlobalLink(b.button, categoryData, locale?.alias || locale?.id),
              )

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useStyleConfig('Sorting')

    const consistentStatus =
        typeof window !== 'undefined' &&
        ((isMobile && window.innerWidth < 768) || (!isMobile && window.innerWidth >= 768))

    const loadingSpinner = (
        <LoadingSpinner
            wrapperStyles={{
                position: 'relative',
                minH: 'inherit',
                background: 'none',
            }}
            spinnerStyles={{
                top: '50%',
            }}
        />
    )

    const CONTAINERS_SELECTOR = `.${componentUniqueKey} .sorting-tile`
    const RESIZABLES_SELECTOR = '.sorting-resizable'
    const DEBOUNCE_WAIT = 200
    const debouncedResize = debounce(() => {
        const containers = [...document.querySelectorAll(CONTAINERS_SELECTOR)]
        const containersResizablesHeights = containers.map((container) =>
            [...container.querySelectorAll(RESIZABLES_SELECTOR)].map((resizable) => {
                resizable.style.minHeight = null
                return resizable.offsetHeight
            }),
        )
        const maxHeights = new Array(Math.max(...containersResizablesHeights.map((c) => c.length)))
            .fill(null)
            .map((_, i) => Math.max(...containersResizablesHeights.map((h) => h[i])))
        containers.forEach((container) => {
            Array.from(container.querySelectorAll(RESIZABLES_SELECTOR)).forEach((resizable, i) => {
                resizable.style.minHeight = remCalc(maxHeights[i])
            })
        })
    }, DEBOUNCE_WAIT)

    useEffect(() => {
        if (!isSlider) {
            debouncedResize()
            window.addEventListener('resize', debouncedResize)

            return () => window.removeEventListener('resize', debouncedResize)
        }
    }, [hasSlider])

    return (
        <Flex
            className={componentUniqueKey}
            {...styles.main}
            data-testid="sorting"
            backgroundColor={variant === 'black' ? 'background.dark' : 'background.default'}
        >
            <Flex {...styles.container}>
                {heading && (
                    <Text
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                        {...styles.heading}
                    >
                        {heading}
                    </Text>
                )}
                {bodycopy && (
                    <SanitizedHtml
                        {...styles.bodycopy}
                        as={Box}
                        html={bodycopy}
                        color={variant === 'black' ? 'text.inverse' : 'text.default'}
                    />
                )}
                {isSlider ? (
                    <Box
                        data-testid="sorting-swiper"
                        {...styles.emptyTiles}
                        __css={{
                            ...swiperStyles,
                            '.swiper-button-disabled': {
                                ...swiperStyles['.swiper-button-disabled'],
                                display: 'none',
                            },
                            '.swiper-slide': {
                                ...swiperStyles['.swiper-slide'],
                                height: 'auto',
                            },
                            '.swiper-pagination-bullet-active': {
                                ...swiperStyles['.swiper-pagination-bullet-active'],
                                background:
                                    variant === 'black' ? 'background.default' : 'background.dark',
                            },
                        }}
                    >
                        {consistentStatus ? (
                            <Swiper
                                modules={[Navigation, Pagination, Keyboard]}
                                navigation={{
                                    prevEl: `.swiper-button-prev-${componentUniqueKey}`,
                                    nextEl: `.swiper-button-next-${componentUniqueKey}`,
                                }}
                                pagination={{
                                    clickable: true,
                                    el: `.swiper-pagination-${componentUniqueKey}`,
                                }}
                                keyboard={{
                                    enabled: true,
                                }}
                                centerInsufficientSlides={true}
                                loop={isMobile ? sortingGroup.length > 1 : sortingGroup.length > 4}
                                breakpoints={{
                                    0: {
                                        slidesPerView: 1,
                                        enabled: sortingGroup.length > 1,
                                        spaceBetween: 0,
                                    },
                                    768: {
                                        slidesPerView: 4,
                                        enabled: sortingGroup.length > 4,
                                        spaceBetween: 16,
                                    },
                                    1024: {
                                        slidesPerView: 4,
                                        enabled: sortingGroup.length > 4,
                                        spaceBetween: 24,
                                    },
                                }}
                                onInit={() => {
                                    debouncedResize()
                                }}
                                onResize={() => {
                                    debouncedResize()
                                }}
                            >
                                {sortingGroup.map((tile, tIndex) => (
                                    <SwiperSlide
                                        key={`swiper-tile-${componentUniqueKey}-${tIndex}`}
                                    >
                                        <SortingTile
                                            key={`sorting-tile-${componentUniqueKey}-${tIndex}`}
                                            styles={styles}
                                            tile={tile}
                                            variant={variant}
                                        />
                                    </SwiperSlide>
                                ))}
                                <ChevronLeft
                                    className={`swiper-button-prev swiper-button-prev-${componentUniqueKey}`}
                                    aria-label="swiper button prev icon"
                                    {...styles.chevronIcon}
                                    left={0}
                                    borderColor={
                                        variant === 'black' ? 'text.inverse' : 'text.default'
                                    }
                                />
                                <ChevronRight
                                    className={`swiper-button-next swiper-button-next-${componentUniqueKey}`}
                                    aria-label="swiper button next icon"
                                    {...styles.chevronIcon}
                                    right={0}
                                    borderColor={
                                        variant === 'black' ? 'text.inverse' : 'text.default'
                                    }
                                />
                                <Box
                                    className={`swiper-pagination-${componentUniqueKey}`}
                                    py={remCalc(24)}
                                />
                            </Swiper>
                        ) : (
                            loadingSpinner
                        )}
                    </Box>
                ) : (
                    <Flex
                        data-testid="sorting-noswiper"
                        {...styles.emptyTiles}
                        {...styles.noswipertiles}
                    >
                        {consistentStatus
                            ? sortingGroup.map((tile, tIndex) => (
                                  <Box
                                      key={`box-tile-${componentUniqueKey}-${tIndex}`}
                                      {...styles.flexbasis}
                                  >
                                      <SortingTile
                                          key={`sorting-tile-${componentUniqueKey}-${tIndex}`}
                                          styles={styles}
                                          tile={tile}
                                          variant={variant}
                                      />
                                  </Box>
                              ))
                            : loadingSpinner}
                    </Flex>
                )}
                {buttonsProps.length > 0 && (
                    <Flex
                        flexDirection={{base: 'column', md: 'row'}}
                        {...styles.ctas}
                        justifyContent={{base: 'flex-start', md: 'center'}}
                        alignItems={{base: 'center', md: 'flex-start'}}
                    >
                        {buttonsProps.map((b, bIndex) => {
                            const bVariant = `${variant === 'black' ? 'inverse-' : ''}${
                                bIndex === 0 ? 'primary' : 'secondary'
                            }`
                            const bProps = b.isAbsolute
                                ? {
                                      as: 'a',
                                      href: b.url,
                                      target: '_blank',
                                      rel: 'noopener noreferrer',
                                  }
                                : {
                                      as: Link,
                                      to: b.url || '',
                                  }

                            return b.label && b.url ? (
                                <Button
                                    key={`sorting-button-${componentUniqueKey}-${bIndex}`}
                                    variant={bVariant}
                                    {...(bVariant !== 'inverse-secondary'
                                        ? styles.cta
                                        : styles.ctaCustom)}
                                    {...bProps}
                                >
                                    {b.label}
                                </Button>
                            ) : null
                        })}
                    </Flex>
                )}
            </Flex>
        </Flex>
    )
}

Sorting.propTypes = {
    title: PropTypes.string,
    variant: PropTypes.string,
    hasSlider: PropTypes.bool,
    heading: PropTypes.string,
    bodycopy: PropTypes.string,
    sortingGroup: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            body_copy: PropTypes.string,
            image: PropTypes.shape(),
            features: PropTypes.arrayOf(PropTypes.shape({feature: PropTypes.string})),
            button: PropTypes.shape(),
        }),
    ),
    buttons: PropTypes.arrayOf(PropTypes.shape()),
}
